import { Dispatch, SetStateAction } from "react"

import {
  AudioVideoFacade,
  DefaultBrowserBehavior,
  PermissionDeniedError,
  VoiceFocusDeviceTransformer,
} from "amazon-chime-sdk-js"

import { HandleAudioRoomError } from "./types"

export const buildDidStartHandler = (
  audioVideo: AudioVideoFacade,
  setConnected: Dispatch<SetStateAction<boolean>>,
  handleError: HandleAudioRoomError,
  onFatalError: (message: string) => void,
  voiceFocusTransformer: VoiceFocusDeviceTransformer | null,
  audioInputDeviceId: string
) => {
  return async () => {
    try {
      const browserBehavior = new DefaultBrowserBehavior()
      if (browserBehavior.supportsSetSinkId()) {
        const outputDevices = await audioVideo.listAudioOutputDevices()
        await audioVideo.chooseAudioOutput(outputDevices[0]?.deviceId)
      }

      const audioElement =
        document.querySelector<HTMLAudioElement>("#room-audio")
      audioVideo.bindAudioElement(audioElement)
      setTimeout(() => audioElement.play(), 500)

      const inputDevices = await audioVideo.listAudioInputDevices()
      const audioInput = inputDevices.find(
        device => device.deviceId === audioInputDeviceId
      )

      if (!audioInput) {
        onFatalError(
          "No microphone found. Please ensure your microphone is connected, then refresh to try again."
        )
        return
      }

      let audioInputDevice: Parameters<AudioVideoFacade["startAudioInput"]>[0] =
        audioInput.deviceId

      if (voiceFocusTransformer) {
        try {
          const voiceFocusDevice =
            await voiceFocusTransformer.createTransformDevice(
              audioInput.deviceId
            )
          if (voiceFocusDevice) {
            audioInputDevice = voiceFocusDevice
          } else {
            handleError({
              airbrakeMessage: "Voice Focus transform device was null",
            })
          }
        } catch (error) {
          handleError({
            airbrakeMessage: "Failed to apply Voice Focus",
            error,
          })
        }
      }

      await audioVideo.startAudioInput(audioInputDevice)
      setConnected(true)
    } catch (error) {
      if (error instanceof PermissionDeniedError)
        onFatalError(
          "Microphone permissions denied. Please enable permissions and then refresh to try again."
        )
      else onFatalError("Something went wrong. Please refresh to try again.")
      handleError({
        airbrakeMessage: "Error caught in buildDidStartHandler",
        error,
      })
    }
  }
}

export const createVoiceFocusTransformer = async (
  handleError: HandleAudioRoomError
): Promise<VoiceFocusDeviceTransformer | null> => {
  try {
    const isSupported = await VoiceFocusDeviceTransformer.isSupported()
    if (!isSupported) {
      handleError({ airbrakeMessage: "Voice Focus unsupported" })
      return null
    }

    const transformer = await VoiceFocusDeviceTransformer.create()
    if (!transformer.isSupported()) {
      handleError({ airbrakeMessage: "Voice Focus transformer unsupported" })
      return null
    }

    return transformer
  } catch (error) {
    handleError({
      airbrakeMessage: "Voice Focus initialization failed",
      error,
    })
    return null
  }
}
